import { CameraAnimationProps } from "@/components/r3f/animations/camera-animation";
import { selectCameraRotationForOdometricPathPano } from "@/components/r3f/animations/to-pano-animation";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { selectMarkupImagePointWorldPosition } from "@/store/select-markup-image-point-world-position";
import { RootState } from "@/store/store";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import { isOrthographicCamera } from "@/types/threejs-type-guards";
import {
  CameraView,
  selectBestCameraViewForElementInThreeSpace,
  selectPanoCamView,
} from "@/utils/cam-view";
import { selectPanoCameraTransform } from "@/utils/camera-transform";
import { parseVector3 } from "@faro-lotv/app-component-toolbox";
import {
  GUID,
  IElement,
  IElementGenericImgSheet,
  IElementImg360,
  isIElementGenericAnnotation,
  isIElementImg2d,
} from "@faro-lotv/ielement-types";
import { assert, DEFAULT_PERSP_FOV, isSupportedCamera } from "@faro-lotv/lotv";
import {
  IElementsState,
  selectAncestor,
  selectIElementWorldPosition,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import type { RefObject } from "react";
import {
  Camera,
  Matrix4,
  OrthographicCamera,
  PerspectiveCamera,
  Quaternion,
  Vector3,
  Vector3Tuple,
} from "three";
import {
  ANIMATION_DURATION,
  FlyWalkSceneActiveElement,
  isFlyWalkImageElement,
} from "../flywalk-types";
import {
  FlyWalkAnimation,
  FlyWalkRenderingAnimation,
} from "./flywalk-animation";

export type TargetCameraView = {
  elementId: GUID;
  cameraView?: CameraView;
  lookAtPosition?: Vector3;
  aspectRatio?: number;
};

/**
 * Computes a suitable fly walk animation to transition the camera to a pano image, and sets it via the given function
 *
 * @param main The pano that is target for the animation
 * @param previousMain The iElements that is rendered at the start of the animation
 * @param renderingAnimation The rendering animation for flywalk mode. Rendering
 * animation parameters are also edited by this function.
 * @param activeSheetFor2DNavigation The reference sheet to compute pano poses
 * @param camera The camera used to render the scene
 * @param pendingEvent The pending event that might trigger a camera animation
 * @param lookAtElement Optional element to look at when the camera arrives in the target pano
 * @param getState Function to get the current state of the app store
 * @param setFlyWalkAnimation Function to set the current flywalk animation
 * @param defaultOrthoCamera The default orthographic camera to use for 2D images
 */
export function runToPanoAnimation(
  main: IElementImg360,
  previousMain: FlyWalkSceneActiveElement | null,
  renderingAnimation: FlyWalkRenderingAnimation,
  activeSheetFor2DNavigation: IElementGenericImgSheet | undefined,
  camera: Camera,
  pendingEvent: RefObject<TargetCameraView | null>,
  lookAtElement: IElement | undefined,
  getState: () => RootState,
  setFlyWalkAnimation: (animation: FlyWalkAnimation | null) => void,
  defaultOrthoCamera: OrthographicCamera,
): void {
  if (isOrthographicCamera(camera)) {
    // Early return since a pano image requires a perspective camera to be rendered.
    // Caller logic is responsible to enter this function with a perspective camera conveniently set.
    // The hook 'usePerspectiveCameraForNonImages' guarantees that this function will be called
    // with a perspective camera eventually, since the main element is a pano image.
    return;
  }
  let targetCameraView: TargetCameraView | undefined;
  if (pendingEvent.current) {
    targetCameraView = pendingEvent.current;
    pendingEvent.current = null;
  }

  const cameraAnimation = selectAnimationToPano(
    main,
    previousMain,
    targetCameraView,
    activeSheetFor2DNavigation,
    lookAtElement,
  )(getState());

  const previousIsFlatImage =
    previousMain &&
    (isIElementImg2d(previousMain) || isIElementOrthomosaic(previousMain));

  runToImgAnimation(
    cameraAnimation,
    camera,
    renderingAnimation,
    previousMain,
    setFlyWalkAnimation,
    true,
    previousIsFlatImage ? defaultOrthoCamera.clone() : undefined,
  );
}

/**
 *
 * @param cameraAnimation The camera animation to execute
 * @param camera The camera rendering the scene
 * @param renderingAnimation The rendering animation to execute
 * @param previousMain The element that was previously the main
 * @param setFlyWalkAnimation callback issued to set the new animation as active
 * @param animateTargetCamera Whether to animate the target camera
 * @param previousCamera An optional camera to be used to render the source element during the animation.
 */
export function runToImgAnimation(
  cameraAnimation: CameraAnimationProps,
  camera: Camera,
  renderingAnimation: FlyWalkRenderingAnimation,
  previousMain: FlyWalkSceneActiveElement | null,
  setFlyWalkAnimation: (animation: FlyWalkAnimation | null) => void,
  animateTargetCamera: boolean = false,
  previousCamera?: Camera,
): void {
  // 'cameraAnimation' can be a memoized selector result, so it must not be mutated here
  const quaternion = cameraAnimation.quaternion ?? camera.quaternion.clone();

  assert(
    !Array.isArray(quaternion),
    "Expected the camera quaternion to be a Quaternion, not an array",
  );

  const equalQuaternions =
    Math.abs(quaternion.dot(camera.quaternion) - 1) < CAMERA_POS_EPSILON;

  if (
    cameraAnimation.position &&
    parseVector3(cameraAnimation.position).distanceToSquared(camera.position) <
      CAMERA_POS_EPSILON &&
    equalQuaternions
  ) {
    setFlyWalkAnimation({
      renderingAnimation,
    });
    return;
  }

  if (previousCamera) {
    assignAspectRatio(previousCamera, camera);
  }
  renderingAnimation.previousCamera = previousCamera ?? camera.clone();
  const targetCamera = camera.clone();
  assert(
    isSupportedCamera(targetCamera),
    "Expected the target camera to be a PerspectiveCamera or an OrthographicCamera",
  );
  renderingAnimation.currentCamera = targetCamera;
  if (!!previousMain && isFlyWalkImageElement(previousMain)) {
    renderingAnimation.start.previousImagePassEnabled = true;
    renderingAnimation.start.panoPassEnabled = false;
  } else if (previousMain === null) {
    renderingAnimation.start.panoPassEnabled = false;
  }
  if (cameraAnimation.position) {
    renderingAnimation.currentCamera.position.copy(
      parseVector3(cameraAnimation.position),
    );
  }

  const animation: FlyWalkAnimation = {
    renderingAnimation,
    cameraAnimation: { ...cameraAnimation, quaternion },
  };

  if (animateTargetCamera) {
    const targetCameraAnimation: CameraAnimationProps = {
      camera: targetCamera,
      quaternion: quaternion.clone(),
      fov: cameraAnimation.fov,
      frustum: cameraAnimation.frustum,
      duration: cameraAnimation.duration,
    };
    animation.targetCameraAnimation = targetCameraAnimation;
  } else {
    renderingAnimation.currentCamera.quaternion.copy(quaternion);
  }

  setFlyWalkAnimation(animation);
}

export const selectAnimationToPano = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (iElements: OnlyIElementsStore, pano: IElementImg360) => pano,
      (
        iElements: OnlyIElementsStore,
        pano: IElementImg360,
        previousMain: FlyWalkSceneActiveElement | null,
      ) => previousMain,
      (
        iElements: OnlyIElementsStore,
        pano: IElementImg360,
        previousMain: FlyWalkSceneActiveElement | null,
        targetCameraView: TargetCameraView | undefined,
      ) => targetCameraView,
      (
        iElements: OnlyIElementsStore,
        pano: IElementImg360,
        previousMain: FlyWalkSceneActiveElement | null,
        targetCameraView: TargetCameraView | undefined,
        sheetForElevation: IElementGenericImgSheet | undefined,
      ) => sheetForElevation,
      (
        iElements: OnlyIElementsStore,
        pano: IElementImg360,
        previousMain: FlyWalkSceneActiveElement | null,
        targetCameraView: TargetCameraView | undefined,
        sheetForElevation: IElementGenericImgSheet | undefined,
        lookAtElement: IElement | undefined,
      ) => lookAtElement,
    ],
    computeAnimationToPano,
  ),
);

function computeAnimationToPano(
  iElements: IElementsState,
  pano: IElementImg360,
  previousMain: FlyWalkSceneActiveElement | null,
  targetCameraView: TargetCameraView | undefined,
  sheetForElevation: IElementGenericImgSheet | undefined,
  lookAtElement: IElement | undefined,
): CameraAnimationProps {
  const targetPose = selectPanoCameraTransform(
    pano,
    sheetForElevation,
  )({ iElements });

  const pathRotation = selectCameraRotationForOdometricPathPano(pano)({
    iElements,
  });

  // Trajectory panos have no saved camera view, so orienting along the path is only a default
  // for when we arrive from a view without a meaningful 3D direction to preserve.
  const isComingFrom3DView =
    !!previousMain &&
    !isIElementImg2d(previousMain) &&
    !isIElementOrthomosaic(previousMain);

  const rotation = new Quaternion();
  let setRotation: boolean = true;

  let fov = DEFAULT_PERSP_FOV;

  const panoCamViewLookAt = selectPanoCamView(pano)({ iElements });

  if (targetCameraView?.cameraView) {
    rotation.copy(targetCameraView.cameraView.rotation);
    if (targetCameraView.cameraView.type === "perspective") {
      fov = targetCameraView.cameraView.zoomLevel;
    }
  } else if (targetCameraView?.lookAtPosition) {
    TARGET_POSITION.fromArray(targetPose.position);
    rotation.setFromRotationMatrix(
      ROTATION_MATRIX.lookAt(
        TARGET_POSITION,
        targetCameraView.lookAtPosition,
        Y_UP,
      ),
    );
  } else if (
    pathRotation &&
    !isComingFrom3DView &&
    (!lookAtElement || lookAtElement.id === pano.id)
  ) {
    rotation.copy(pathRotation);
  } else if (lookAtElement) {
    const lookAtCameraView = selectBestCameraViewForElementInThreeSpace(
      lookAtElement.id,
    )({
      iElements,
    });
    if (lookAtCameraView) {
      rotation.copy(lookAtCameraView.rotation);
    } else {
      const annotationWorldPositionTuple = selectAnnotationPosition(
        lookAtElement,
        pano,
      )({
        iElements,
      });

      // Selecting the original position of the annotation to look at, if it exists
      const elementWorldPos = selectIElementWorldPosition(lookAtElement.id)({
        iElements,
      });

      TARGET_POSITION.fromArray(targetPose.position);
      // Check if the element had 2D coordinates that are now converted in 3D world coordinates
      if (annotationWorldPositionTuple) {
        TARGET_LOOK_AT.fromArray(annotationWorldPositionTuple);
      } else {
        const panoOrigPos = selectIElementWorldPosition(pano.id)({
          iElements,
        });
        const diff = new Vector3(
          elementWorldPos[0] - panoOrigPos[0],
          elementWorldPos[1] - panoOrigPos[1],
          elementWorldPos[2] - panoOrigPos[2],
        );
        TARGET_LOOK_AT.addVectors(diff, TARGET_POSITION);
      }
      rotation.setFromRotationMatrix(
        ROTATION_MATRIX.lookAt(TARGET_POSITION, TARGET_LOOK_AT, Y_UP),
      );
    }
  } else if (panoCamViewLookAt) {
    rotation.copy(panoCamViewLookAt.rotation);
    if (panoCamViewLookAt.type === "perspective") {
      fov = panoCamViewLookAt.zoomLevel;
    }
  } else if (previousMain === null || isIElementOrthomosaic(previousMain)) {
    TARGET_POSITION.fromArray(targetPose.position);
    TARGET_LOOK_AT.addVectors(TARGET_POSITION, DEFAULT_LOOK_AT_DIR);
    rotation.setFromRotationMatrix(
      ROTATION_MATRIX.lookAt(TARGET_POSITION, TARGET_LOOK_AT, Y_UP),
    );
  } else {
    // If we are transitioning from a pano, or from a photo, or from a model, we keep the current camera rotation.
    setRotation = false;
  }

  return {
    position: targetPose.position,
    quaternion: setRotation ? rotation : undefined,
    duration: ANIMATION_DURATION,
    fov,
  };
}

/** A reusable rotation Matrix4 */
const ROTATION_MATRIX = new Matrix4();

/** A Vector3 indicating y-up */
const Y_UP = new Vector3(0, 1, 0);

/** A reuseable vector used to hold the target position */
const TARGET_POSITION = new Vector3();

/** A reusable vector which indicates where the camera should look at */
const TARGET_LOOK_AT = new Vector3();

/** The default camera orientation when opening a pano for the first time */
const DEFAULT_LOOK_AT_DIR = new Vector3(-1, 0, 0);

/** Min distance below which two positions are considered coincident */
const CAMERA_POS_EPSILON = 1e-6;

/**
 * @param iElements the whole iElements state
 * @param lookAt the element to look at during the pano animation.
 * @param pano the panorama image element
 * @returns the annotation position if available
 */
const selectAnnotationPosition = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (iElements: OnlyIElementsStore, lookAt: IElement | undefined) => lookAt,
      (
        iElements: OnlyIElementsStore,
        lookAt: IElement | undefined,
        pano: IElementImg360,
      ) => pano,
    ],
    computeAnnotationPosition,
  ),
);

function computeAnnotationPosition(
  iElements: IElementsState,
  lookAt: IElement | undefined,
  pano: IElementImg360,
): Vector3Tuple | undefined {
  const state = { iElements };

  if (!lookAt) return;

  // Check if the element has a parent annotation
  const annotation = selectAncestor(lookAt, isIElementGenericAnnotation)(state);
  if (!annotation) return;

  // Get the 2D annotation world position, if available
  return selectMarkupImagePointWorldPosition(
    annotation,
    pano,
  )(state)?.toArray();
}

function findAspect(camera: Camera): number {
  if (camera instanceof PerspectiveCamera) {
    return camera.aspect;
  }
  if (camera instanceof OrthographicCamera) {
    return (camera.right - camera.left) / (camera.top - camera.bottom);
  }
  return 1;
}

function assignAspectRatio(target: Camera, source: Camera): void {
  const aspect = findAspect(source);
  if (target instanceof PerspectiveCamera) {
    target.aspect = aspect;
    target.updateProjectionMatrix();
  } else if (target instanceof OrthographicCamera) {
    const centerX = (target.left + target.right) * 0.5;
    const halfWidth = (target.top - target.bottom) * aspect * 0.5;
    target.left = centerX - halfWidth;
    target.right = centerX + halfWidth;
    target.updateProjectionMatrix();
  }
}
